import React from 'react';
import { Box, Grid, Paper, Typography, Chip } from '@mui/material';
import { getCriteriaVariableLabel } from '../../../pages/ViewGoal/Insights/Criteria/utils/criteriaUtils';
import {
  FormatListBulleted,
  FlashOn,
  AccessTime,
  CalendarToday,
} from '@mui/icons-material';
import { formatPromptingLevel } from './utils';

const SubgoalPromptItem = ({ label, value, xs = 3 }) => (
  <Grid item xs={6} sm={xs}>
    <Paper
      variant='outlined'
      sx={{
        p: 2.5,
        bgcolor: '#f8f9ff',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': { backgroundColor: '#f0f2ff', borderColor: '#9291ef' },
      }}
    >
      <Typography
        variant='body2'
        sx={{ color: '#6c757d', fontWeight: 600, mb: 1 }}
      >
        {label}
      </Typography>
      <Typography
        variant='body1'
        component='span'
        sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '1rem' }}
      >
        {value}
      </Typography>
    </Paper>
  </Grid>
);

const SubgoalsTab = ({ goalData }) => {
  return (
    <Grid container spacing={4}>
      {goalData?.HasSubgoal?.Subgoals?.length > 0 ? (
        goalData?.HasSubgoal?.Subgoals?.map((subgoal, index) => (
          <Grid item xs={12} key={index}>
            <Paper
              elevation={0}
              variant='outlined'
              sx={{
                p: 4,
                mb: 4,
                borderRadius: '20px',
                border: '1px solid #e0e0e0',
                position: 'relative',
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(146, 145, 239, 0.15)',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant='h5'
                  sx={{ fontWeight: 'bold', color: '#2c3e50' }}
                >
                  Subgoal #{index + 1}
                  {subgoal?.CurrentGoal === 'Yes' && (
                    <Chip
                      label='Current'
                      color='primary'
                      size='medium'
                      sx={{
                        ml: 3,
                        backgroundColor: '#9291ef',
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Typography>
              </Box>

              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  mb: 4,
                  bgcolor: '#f8f9ff',
                  borderRadius: '16px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: '#6c757d' }}
                  >
                    Target
                  </Typography>
                </Box>
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 500, fontSize: '1.1rem', lineHeight: 1.6 }}
                >
                  {subgoal?.Target === 'Atmost'
                    ? 'At most'
                    : subgoal?.Target === 'Atleast'
                    ? 'At least'
                    : subgoal?.Target}{' '}
                  <Box
                    component='span'
                    sx={{ fontWeight: 'bold', color: '#9291ef' }}
                  >
                    {subgoal?.Measurement}{' '}
                  </Box>
                  {subgoal?.ConsecutiveTargetDuration && 'consecutive '}
                  {goalData?.DataCollection?.measurementType?.Type ===
                    'Frequency' && 'times'}
                  {goalData?.DataCollection?.measurementType?.Type ===
                    'Duration' && 'mins'}
                  {goalData?.DataCollection?.measurementType?.Type ===
                    'Percentage' && '%'}
                  {goalData?.DataCollection?.measurementType?.DataType}
                  {goalData?.DataCollection?.measurementType?.Type ===
                    'Prompting' &&
                    subgoal?.PromptingLevel && (
                      <>
                        {' with '}
                        <Box
                          component='span'
                          sx={{ fontWeight: 'bold', color: '#9291ef' }}
                        >
                          {formatPromptingLevel(subgoal?.PromptingLevel)}
                        </Box>{' '}
                      </>
                    )}
                  {goalData?.DataCollection?.measurementType?.Type ===
                    'Trials' &&
                    ` in ${goalData?.DataCollection?.measurementType?.TrailsValue?.TrailsMin} out of ${goalData?.DataCollection?.measurementType?.TrailsValue?.TrailsMax} trials`}
                  .
                </Typography>
              </Paper>

              {subgoal?.Criteria?.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FormatListBulleted
                      fontSize='small'
                      color='action'
                      sx={{ mr: 1 }}
                    />
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, color: '#6c757d' }}
                    >
                      Additional Criteria
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {subgoal?.Criteria?.map((criteria, criteriaIndex) => (
                      <Paper
                        key={criteriaIndex}
                        variant='outlined'
                        sx={{
                          p: 3,
                          backgroundColor: '#e9eaff',
                          borderRadius: '12px',
                        }}
                      >
                        <Typography
                          variant='body1'
                          sx={{ fontSize: '1rem', lineHeight: 1.6 }}
                        >
                          {criteria.Target === 'Atmost'
                            ? 'At most'
                            : criteria.Target === 'Atleast'
                            ? 'At least'
                            : criteria.Target}{' '}
                          <Box component='span' sx={{ fontWeight: 'bold' }}>
                            {criteria.TargetDuration}{' '}
                          </Box>
                          {criteria.Consecutive && 'consecutive '}
                          {criteria?.MeasurementType === 'Frequency' &&
                            'times of '}
                          {criteria?.MeasurementType === 'Duration' &&
                            'mins of '}
                          {criteria?.MeasurementType === 'Percentage' &&
                            '% of '}
                          <Box component='span' sx={{ fontWeight: 'bold' }}>
                            {getCriteriaVariableLabel(criteria)}
                          </Box>
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}

              {subgoal?.PromptingApplicable?.isPromptingApplicable ===
                'Yes' && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FlashOn fontSize='small' color='action' sx={{ mr: 1 }} />
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, color: '#6c757d' }}
                    >
                      Prompting: {subgoal?.PromptingApplicable?.PromptingType}
                    </Typography>
                  </Box>
                  {(subgoal?.PromptingApplicable?.PromptingType ===
                    'IVP, DVP, PVP, GP, PP' ||
                    subgoal?.PromptingApplicable?.PromptingType ===
                      'I, VP (IVP, DVP, PVP), GP, PP, ViP, M') && (
                    <Grid container spacing={2}>
                      <SubgoalPromptItem
                        label='I'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.I
                        )}
                      />
                      <SubgoalPromptItem
                        label='VP'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.VP
                        )}
                      />
                      <SubgoalPromptItem
                        label='IVP'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.IVP
                        )}
                      />
                      <SubgoalPromptItem
                        label='DVP'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.DVP
                        )}
                      />
                      <SubgoalPromptItem
                        label='PVP'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.PVP
                        )}
                      />
                      <SubgoalPromptItem
                        label='GP'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.GP
                        )}
                      />
                      <SubgoalPromptItem
                        label='PP'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.PP
                        )}
                      />
                      <SubgoalPromptItem
                        label='ViP'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.ViP
                        )}
                      />
                      <SubgoalPromptItem
                        label='M'
                        value={formatPromptingLevel(
                          subgoal?.PromptingApplicable?.M
                        )}
                      />
                    </Grid>
                  )}
                  {subgoal?.PromptingApplicable?.PromptingType ===
                    'P+ & P-' && (
                    <Grid container spacing={3}>
                      <SubgoalPromptItem
                        label='P+'
                        value={subgoal?.PromptingApplicable?.pPlus}
                        xs={6}
                      />
                      <SubgoalPromptItem
                        label='P-'
                        value={subgoal?.PromptingApplicable?.pMinus}
                        xs={6}
                      />
                    </Grid>
                  )}
                </Box>
              )}

              {subgoal?.MultipleTimesToMeetGoal?.isMultipleTimesToMeetGoal ===
                'Yes' && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTime
                      fontSize='small'
                      color='action'
                      sx={{ mr: 1 }}
                    />
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, color: '#6c757d' }}
                    >
                      Multiple Times to Meet Goal
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    {subgoal?.MultipleTimesToMeetGoal?.NumberOfSessions && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant='outlined'
                          sx={{
                            p: 3,
                            borderRadius: '12px',
                            backgroundColor: '#e9eaff',
                          }}
                        >
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            display='block'
                            gutterBottom
                          >
                            Number of times goal has to be achieved
                          </Typography>
                          <Typography
                            variant='h6'
                            fontWeight='bold'
                            color='#2c3e50'
                          >
                            {subgoal?.MultipleTimesToMeetGoal?.NumberOfSessions}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {subgoal?.MultipleTimesToMeetGoal?.ConsecutiveSessions && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant='outlined'
                          sx={{
                            p: 3,
                            borderRadius: '12px',
                            backgroundColor: '#e9eaff',
                          }}
                        >
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            display='block'
                            gutterBottom
                          >
                            Do the sessions have to be consecutive?
                          </Typography>
                          <Typography
                            variant='h6'
                            fontWeight='bold'
                            color='#2c3e50'
                          >
                            {
                              subgoal?.MultipleTimesToMeetGoal
                                ?.ConsecutiveSessions
                            }
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {subgoal?.subGoalByDate && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarToday
                      fontSize='small'
                      color='action'
                      sx={{ mr: 1 }}
                    />
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 600, color: '#6c757d' }}
                    >
                      Due Date
                    </Typography>
                  </Box>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 3,
                      borderRadius: '12px',
                      backgroundColor: '#e9eaff',
                    }}
                  >
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      display='block'
                      gutterBottom
                    >
                      Due by
                    </Typography>
                    <Typography variant='h6' fontWeight='bold' color='#2c3e50'>
                      {new Date(subgoal.subGoalByDate).toLocaleDateString()}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Paper>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Paper
            variant='outlined'
            sx={{
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f8f9ff',
              borderRadius: '20px',
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography
              variant='h6'
              color='text.secondary'
              align='center'
              sx={{ fontWeight: 500 }}
            >
              No subgoals available for this goal.
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default SubgoalsTab;
